import { type ReactElement, Children, isValidElement, type ReactNode } from 'react'
import { type SingleOrOptionalEntityIds, type PermissionType, PERMISSION_DEFINITION } from '@terros/common'
import { usePermissionCheck } from '@front/model'
import { type Condition, ConditionTooltip, type ConditionTooltipProps } from '../ConditionTooltip'

export type PermissionTooltipProps = Omit<ConditionTooltipProps, 'conditions'> & {
  targetId: SingleOrOptionalEntityIds | undefined
  label?: string
  permissionType: PermissionType
  extraConditions?: Condition[]
}

export const PermissionTooltip = ({
  label,
  permissionType,
  targetId,
  extraConditions: overrides,
  ...props
}: PermissionTooltipProps): ReactElement => {
  const { data: hasPermission, isLoading: isCheckingPermission } = usePermissionCheck({ permissionType, targetId })

  const notAllowedActive = isCheckingPermission || !hasPermission

  const action = PERMISSION_DEFINITION[permissionType].action

  const checkList: Condition[] = [
    {
      active: notAllowedActive,
      label: isCheckingPermission ? 'Checking permission' : (label ?? `You do not have permission to ${action}`),
    },
    ...(overrides ?? []),
  ]

  return <ConditionTooltip conditions={checkList} {...props} />
}

const PermissionTooltipGroup: React.FC<Omit<PermissionTooltipProps, 'children'> & { children: ReactNode }> = ({
  children,
  ...props
}) => {
  const validChildren = Children.toArray(children).filter(isValidElement)

  return validChildren.map((child, index) => (
    <PermissionTooltip key={index} {...props}>
      {child}
    </PermissionTooltip>
  ))
}

PermissionTooltipGroup.displayName = 'PermissionTooltip.Group'
PermissionTooltip.Group = PermissionTooltipGroup
